import { useEffect, useState } from 'react';
import { Button, toast } from '@ludolio/shared-ui';
import { getErrorMessage, labsApi } from '@/lib/api';
import type { LabLineage } from '@/lib/types';

export function LineagePanel({ projectId }: { projectId: string }) {
  const [lineage, setLineage] = useState<LabLineage | null>(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    labsApi.lineage(projectId, page)
      .then((result) => {
        if (active) {
          setLineage(result);
          setError('');
        }
      })
      .catch((err) => {
        if (active) setError(getErrorMessage(err));
      });
    return () => {
      active = false;
    };
  }, [page, projectId]);

  const download = async () => {
    const releaseId = lineage?.ancestors[0]?.releaseId;
    if (!releaseId) return;
    setDownloading(true);
    try {
      const result = await labsApi.downloadSource(releaseId);
      window.location.assign(result.url);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  if (error) return <p className="text-sm text-destructive" role="alert">{error}</p>;
  if (!lineage) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const pageCount = Math.max(1, Math.ceil(lineage.children.total / lineage.children.pageSize));

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Originals</h2>
        {lineage.ancestors.length === 0 && (
          <p className="text-sm text-muted-foreground">This project is not a remix.</p>
        )}
        {lineage.ancestors.length > 0 && (
          <ol className="space-y-2">
            {lineage.ancestors.map((ancestor, index) => (
              <li key={ancestor.releaseId} className="text-sm">
                <span className="text-muted-foreground">{index === 0 ? 'Remix of' : 'Which remixed'}</span>
                {' '}
                {ancestor.title} · {ancestor.version}
                {ancestor.organization ? ` · ${ancestor.organization}` : ''}
                {ancestor.withdrawn && <span className="text-muted-foreground"> · taken down</span>}
              </li>
            ))}
          </ol>
        )}
        {lineage.sourceDownload === 'available' && (
          <Button type="button" variant="outline" disabled={downloading} onClick={() => void download()}>
            {downloading ? 'Preparing…' : 'Download original source'}
          </Button>
        )}
        {lineage.sourceDownload === 'closed' && (
          <p className="text-sm text-muted-foreground">
            {lineage.ancestors[0]?.withdrawn
              ? 'Source downloads are closed because the original was taken down.'
              : 'The showcase no longer offers this source.'}
          </p>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Remixes</h2>
        {lineage.children.items.length === 0 && (
          <p className="text-sm text-muted-foreground">No published remixes yet.</p>
        )}
        {lineage.children.items.length > 0 && (
          <ul className="divide-y rounded-lg border">
            {lineage.children.items.map((child) => (
              <li key={child.projectId} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                <span className="min-w-0 truncate">{child.title}</span>
                <span className="shrink-0 text-muted-foreground">
                  {child.version}
                  {child.organization ? ` · ${child.organization}` : ''}
                  {child.withdrawn ? ' · taken down' : ''}
                  {child.parentVersion ? ` · from ${child.parentVersion}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
        {pageCount > 1 && (
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
              Previous
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>
              Next
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
